import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell, PageHero } from "@/components/site/SiteShell";
import { Section } from "@/components/site/Blocks";
import { CheckCircle2, ArrowRight } from "lucide-react";

export const Route = createFileRoute("/thank-you")({
  head: () => ({
    meta: [
      { title: "Thank You — Application Received | Simply Approved Mortgages" },
      { name: "description", content: "Your application is in. Here's what happens next and the documents to gather while our team reviews your file." },
      { name: "robots", content: "noindex, nofollow" },
    ],
    links: [{ rel: "canonical", href: "/thank-you" }],
  }),
  component: ThankYouPage,
});

const nextSteps = [
  "A licensed loan officer will call or email you within one business day.",
  "You'll receive an initial scenario review once we have credit authorization and your supporting documents — formal pricing, LTV, points, and reserves are issued after underwriting.",
  "We'll open title, order the appraisal, and line up insurance once you're moving forward.",
  "A secure document upload link will be sent — no need to email files.",
];


const documents = [
  "Government-issued photo ID for every borrower / guarantor",
  "Two months of most recent bank / brokerage statements (liquid reserves)",
  "Entity docs if closing in an LLC / Corp / LP (Articles, EIN letter, Operating Agreement)",
  "Signed purchase contract or current mortgage statement (refi)",
  "Property tax + insurance bill (or quote)",
  "Rent roll / current leases (if applicable)",
];

function ThankYouPage() {
  return (
    <SiteShell>
      <PageHero
        eyebrow="Application received"
        title="We've got it."
        subtitle="Thank you — we've received your information. We'll review your submission and follow up with next steps to complete your application."
      />
      <Section>
        <div className="mx-auto max-w-3xl space-y-6">
          <div className="rounded-2xl border border-brand-gold/40 bg-brand-gold/10 p-6 text-center">
            <CheckCircle2 className="mx-auto h-10 w-10 text-brand" />
            <p className="mt-2 text-sm text-muted-foreground">
              Your file is queued and routed to our investor-lending desk.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            <div className="rounded-2xl border border-border bg-card p-6">
              <div className="text-[11px] font-black uppercase tracking-widest text-brand">
                What happens next
              </div>
              <p className="mt-2 text-sm text-muted-foreground">
                Initial scenario review typically same day or next business day after credit authorization + documents.
              </p>
              <ul className="mt-3 space-y-2 text-sm">
                {nextSteps.map((s, i) => (
                  <li key={i} className="flex gap-2">
                    <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-2xl border border-border bg-card p-6">
              <div className="text-[11px] font-black uppercase tracking-widest text-brand">
                Documents to gather
              </div>
              <p className="mt-2 text-sm text-muted-foreground">
                We'll send a secure upload link — no need to email files.
              </p>
              <ul className="mt-3 space-y-2 text-sm">
                {documents.map((s, i) => (
                  <li key={i} className="flex gap-2">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand-gold" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="rounded-2xl border border-border bg-card p-6 text-sm text-muted-foreground">
            <p>
              Prefer to talk now? Call{" "}
              <a href="tel:+18778137219" className="font-bold text-brand hover:underline">
                (877) 813-7219
              </a>{" "}
              or{" "}
              <Link to="/contact" className="font-bold text-brand hover:underline">
                send us a message
              </Link>
              .
            </p>

            <p className="mt-3">
              <Link to="/" className="font-bold text-brand hover:underline">
                ← Back to home
              </Link>
            </p>
          </div>
        </div>
      </Section>
    </SiteShell>
  );
}
